import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  PlayArrow as TriggerIcon,
  Event as EventIcon,
  Storage as DataIcon,
  FilterAlt as FilterIcon,
  Description as ExcelIcon,
  SmartToy as AiIcon,
  Build as ToolIcon,
  Message as TeamsIcon,
  Email as OutlookIcon,
  Psychology as AgentIcon,
  TableChart as ModelIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { SideBar } from "../../../components/shared/sideBar/sideBar";
import { useAppTheme } from "../../../components/themes/appThemeProvider";
import { callAPI } from "../../../functions/api";
import { useUser } from "../../../functions/userContext";

// Icon mapping for node types
const getNodeIcon = (icon, category, type) => {
  const iconMap = {
    // Triggers
    trigger: <TriggerIcon />,
    event: <EventIcon />,
    schdeule: <ScheduleIcon />, // Note: API has typo "schdeule"
    schedule: <ScheduleIcon />,

    // Data
    model: <ModelIcon />,
    filter: <FilterIcon />,
    excel: <ExcelIcon />,

    // AI & Tools
    agent: <AgentIcon />,
    azure: <AiIcon />,

    // Communications
    teams: <TeamsIcon />,
    outlook: <OutlookIcon />,
  };

  return iconMap[icon] || iconMap[type] || <DataIcon />;
};

// Category styling and info
const getCategoryInfo = (category, isDark) => {
  const categoryMap = {
    trigger: {
      label: "Trigger",
      icon: <TriggerIcon />,
    },
    data: {
      label: "Data",
      icon: <DataIcon />,
    },
    ai: {
      label: "AI",
      icon: <AiIcon />,
    },
    tool: {
      label: "Tools",
      icon: <ToolIcon />,
    },
    comms: {
      label: "Communication",
      icon: <TeamsIcon />,
    },
  };

  return (
    categoryMap[category] || {
      label: category.charAt(0).toUpperCase() + category.slice(1),
      icon: <DataIcon />,
    }
  );
};

export const ReportBuilderViewSideBar = forwardRef(({ onAddNode, children, nodes }, ref) => {
  const innerRef = useRef();
  const { mode } = useAppTheme();
  const { toolsOpen } = useUser();
  const isDark = mode === "dark";
  const [search, setSearch] = useState("");
  const [nodeDefs, setNodeDefs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null); // null shows categories, string shows nodes

  // Reset category selection when sidebar closes (from outside clicks)
  useEffect(() => {
    if (!toolsOpen) {
      setSelectedCategory(null);
    }
  }, [toolsOpen]);

  useImperativeHandle(ref, () => ({
    open: () => innerRef.current?.open(),
    close: () => {
      setSelectedCategory(null); // Reset category selection when closing
      innerRef.current?.close();
    },
    toggle: () => innerRef.current?.toggle(),
  }));

  const fetchNodeDefinitions = async () => {
    try {
      // Always fetch all categories - let user decide what to add
      const types = ["trigger", "data", "ai", "tool", "comms"];

      const res = await callAPI("ai/nodes", "POST", types, false);

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      console.log("Node definitions received:", data);

      const nodeList = Array.isArray(data) ? data : Object.values(data).flat();

      return nodeList.map((n) => ({
        ...n,
        fullType: `${n.category}.${n.type}`,
      }));
    } catch (err) {
      console.error("Failed to fetch node definitions:", err);
      return [];
    }
  };

  useEffect(() => {
    fetchNodeDefinitions().then((defs) => {
      setNodeDefs(defs);
      // Only reset to category view if we don't have node definitions yet
      // This prevents resetting the view when nodes are added to the workflow
      if (nodeDefs.length === 0) {
        setSelectedCategory(null);
      }
    });
  }, []); // Remove nodes dependency to prevent reset when workflow nodes change

  // Filter nodes based on search and selected category
  const filteredNodes = nodeDefs.filter((node) => {
    const matchesSearch =
      search === "" ||
      node.label.toLowerCase().includes(search.toLowerCase()) ||
      node.description.toLowerCase().includes(search.toLowerCase());

    if (selectedCategory) {
      return node.category === selectedCategory && matchesSearch;
    }

    return matchesSearch;
  });

  // Get available categories with counts
  const availableCategories = [...new Set(nodeDefs.map((n) => n.category))].map((category) => ({
    name: category,
    count: nodeDefs.filter((n) => n.category === category).length,
    ...getCategoryInfo(category, isDark),
  }));

  const handleSelectNode = (nodeDef) => {
    // Keep the original node structure - don't modify the type field
    if (onAddNode) onAddNode(nodeDef);
    // Keep sidebar open so users can continue adding nodes
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategory(category);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
  };

  const NodeCard = ({ node }) => {
    const categoryInfo = getCategoryInfo(node.category, isDark);

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          mb: 1,
          borderRadius: "8px",
          cursor: "pointer",
          transition: "all 0.15s ease",
          backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
          border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)"}`,
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
            borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.12)",
            transform: "translateY(-1px)",
          },
        }}
        onClick={() => handleSelectNode(node)}
        onDoubleClick={() => handleSelectNode(node)}
        draggable
        onDragStart={(e) => {
          // Use the same data transfer format that the canvas expects
          e.dataTransfer.setData("application/reactflow", JSON.stringify(node));
          e.dataTransfer.effectAllowed = "copy";
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: "8px",
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
            color: isDark ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.7)",
            flexShrink: 0,
          }}
        >
          {/* {getNodeIcon(node.icon, node.category, node.type)} */}
          <img
            src={require(`../../../assets/${node.icon}.png`)}
            alt="Node Icon"
            style={{ height: "25px" }}
          />
        </Box>

        <Box flex={1} sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb: 0.5,
              color: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {node.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "12px",
              color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {node.description}
          </Typography>
        </Box>
      </Box>
    );
  };

  const CategoryCard = ({ category }) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          mb: 1,
          borderRadius: "8px",
          cursor: "pointer",
          transition: "all 0.15s ease",
          backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
          border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)"}`,
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
            borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.12)",
            transform: "translateY(-1px)",
          },
        }}
        onClick={() => handleCategoryToggle(category.name)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: "8px",
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
            color: isDark ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.7)",
            flexShrink: 0,
          }}
        >
          {category.icon}
        </Box>

        <Box flex={1} sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb: 0.5,
              color: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
            }}
          >
            {category.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "12px",
              color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
            }}
          >
            {category.count} {category.count === 1 ? "node" : "nodes"}
          </Typography>
        </Box>

        <Box
          sx={{
            color: isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
            fontSize: "18px",
          }}
        >
          ›
        </Box>
      </Box>
    );
  };

  return (
    <SideBar ref={innerRef}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "calc(100% + 20px)",
          overflow: "hidden",
          marginLeft: "-10px",
          marginRight: "-10px",
        }}
      >
        {/* Header */}
        <Box sx={{ px: 1, py: 1.5, flexShrink: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              marginLeft: "5px",
              color: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
              fontSize: "18px",
            }}
          >
            What happens next?
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ px: 1, pb: 1, flexShrink: 0 }}>
          <TextField
            size="small"
            placeholder={
              selectedCategory
                ? `Search ${availableCategories.find((cat) => cat.name === selectedCategory)?.label || selectedCategory} nodes...`
                : "Search all nodes..."
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                },
                "&:hover fieldset": {
                  borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
                },
              },
              "& .MuiInputBase-input": {
                color: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
                fontSize: "14px",
              },
              "& .MuiInputBase-input::placeholder": {
                color: isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)" }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Main Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 1,
            py: 1,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
            },
          }}
        >
          {selectedCategory ? (
            <>
              {/* Category Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2,
                  px: 1,
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleBackToCategories}
                  sx={{
                    padding: 0.5,
                    color: isDark ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)",
                    "&:hover": {
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
                  }}
                >
                  {availableCategories.find((cat) => cat.name === selectedCategory)?.label}
                </Typography>
              </Box>

              {/* Nodes List */}
              {filteredNodes.map((node, index) => (
                <NodeCard key={`${node.category}-${node.type}-${index}`} node={node} />
              ))}

              {filteredNodes.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                    textAlign: "center",
                  }}
                >
                  <SearchIcon
                    sx={{
                      fontSize: 48,
                      color: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
                    }}
                  >
                    {search ? "No nodes match your search" : "No nodes available"}
                  </Typography>
                </Box>
              )}
            </>
          ) : search ? (
            <>
              {/* Search Results Header */}
              <Box sx={{ mb: 2, px: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
                  }}
                >
                  Search results for "{search}"
                </Typography>
              </Box>

              {/* Search Results List */}
              {filteredNodes.map((node, index) => (
                <NodeCard key={`${node.category}-${node.type}-${index}`} node={node} />
              ))}

              {filteredNodes.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                    textAlign: "center",
                  }}
                >
                  <SearchIcon
                    sx={{
                      fontSize: 48,
                      color: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
                    }}
                  >
                    No nodes match your search
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <>
              {/* Categories Header */}
              <Box sx={{ mb: 2, px: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)",
                  }}
                >
                  Choose a category
                </Typography>
              </Box>

              {/* Categories Grid */}
              {availableCategories.map((category) => (
                <CategoryCard key={category.name} category={category} />
              ))}
            </>
          )}
        </Box>
      </Box>
    </SideBar>
  );
});
